import {Alert, Anchor, Button, Group, Loader, Select, Stack, Text} from '@mantine/core'
import {useEffect, useMemo, useState} from 'react'

import {useCurrentUser} from '../auth/use-current-user'
import {useEditRights} from '../jobs/edit-rights-context'
import {repOptions} from '../users/rep-options'
import {useProjectContacts} from '../users/use-project-contacts'
import type {ProjectDetail} from './schemas'
import {usePatchProject} from './use-projects'

type ProjectInternalContactSectionProps = {
    project: ProjectDetail
}

export function ProjectInternalContactSection({project}: ProjectInternalContactSectionProps) {
    const {canEdit} = useEditRights()
    const patchProject = usePatchProject(project.id)
    const {data: contacts = [], isLoading, isError, error} = useProjectContacts()
    const internalContactId = project.internalContact?.id ?? null
    const [isEditing, setIsEditing] = useState(false)
    const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
    const [saveError, setSaveError] = useState<string | null>(null)

    const assignedContactIsActive = contacts.some((contact) => contact.id === internalContactId)

    useEffect(() => {
        setSelectedContactId(
            internalContactId && assignedContactIsActive ? internalContactId : null,
        )
    }, [project.id, internalContactId, assignedContactIsActive])

    const currentUserId = useCurrentUser()?.id ?? null
    const selectData = useMemo(
        () => repOptions(contacts, {currentUserId, value: internalContactId}),
        [contacts, currentUserId, internalContactId],
    )

    async function handleSave() {
        if (!selectedContactId) {
            setSaveError('Select an active internal contact')
            return
        }

        setSaveError(null)
        try {
            await patchProject.mutateAsync({
                name: project.name,
                description: project.description,
                notes: project.notes,
                customerId: project.customerId,
                internalContactId: selectedContactId ?? null,
                customerContactId: project.customerContact?.id ?? null,
            })
            setIsEditing(false)
        } catch (saveFailure) {
            setSaveError(
                saveFailure instanceof Error
                    ? saveFailure.message
                    : 'Failed to update internal contact',
            )
        }
    }

    return (
        <Stack gap="md">
            {isEditing ? (
                <Text size="sm" fw={600}>
                    Internal contact
                </Text>
            ) : null}

            {isEditing ? (
                <Stack gap="sm">
                    {internalContactId != null && !assignedContactIsActive ? (
                        <Text size="sm" c="dimmed">
                            Current contact: {project.internalContact?.name ?? 'Unassigned'}
                        </Text>
                    ) : null}
                    {isLoading ? (
                        <Loader size="sm" />
                    ) : isError ? (
                        <Alert color="red" title="Could not load internal contacts">
                            {error instanceof Error ? error.message : 'Something went wrong'}
                        </Alert>
                    ) : (
                        <Select
                            label="Internal contact"
                            placeholder="Select an active internal contact"
                            data={selectData}
                            searchable
                            value={selectedContactId}
                            onChange={setSelectedContactId}
                            error={saveError}
                        />
                    )}
                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            size="compact-sm"
                            onClick={() => {
                                setSelectedContactId(
                                    assignedContactIsActive ? internalContactId : null,
                                )
                                setSaveError(null)
                                setIsEditing(false)
                            }}
                            disabled={patchProject.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="compact-sm"
                            onClick={() => void handleSave()}
                            loading={patchProject.isPending}
                            disabled={isLoading || isError}
                        >
                            Save
                        </Button>
                    </Group>
                </Stack>
            ) : internalContactId != null ? (
                <Group
                    justify="space-between"
                    align="center"
                    wrap="nowrap"
                    p="xs"
                    style={{
                        background: 'var(--mantine-color-gray-0)',
                        border: '1px solid var(--mantine-color-gray-2)',
                        borderRadius: 10,
                    }}
                >
                    <Stack gap={0} style={{flex: 1, minWidth: 0}}>
                        <Text
                            size="xs"
                            fw={700}
                            c="dimmed"
                            tt="uppercase"
                            style={{letterSpacing: '0.04em'}}
                        >
                            Internal contact
                        </Text>
                        <Text size="sm" fw={700}>
                            {project.internalContact?.name}
                        </Text>
                        {project.internalContact?.email ? (
                            <Anchor href={`mailto:${project.internalContact.email}`} size="xs">
                                {project.internalContact.email}
                            </Anchor>
                        ) : null}
                    </Stack>
                    {canEdit ? (
                        <Button
                            variant="subtle"
                            size="compact-sm"
                            onClick={() => setIsEditing(true)}
                        >
                            Reassign
                        </Button>
                    ) : null}
                </Group>
            ) : (
                <Group
                    justify="space-between"
                    align="center"
                    wrap="nowrap"
                    p="xs"
                    style={{border: '1px dashed var(--mantine-color-gray-4)', borderRadius: 10}}
                >
                    <Stack gap={0} style={{flex: 1, minWidth: 0}}>
                        <Text
                            size="xs"
                            fw={700}
                            c="dimmed"
                            tt="uppercase"
                            style={{letterSpacing: '0.04em'}}
                        >
                            Internal contact
                        </Text>
                        <Text size="sm" c="dimmed">
                            Not assigned
                        </Text>
                    </Stack>
                    {canEdit ? (
                        <Button size="compact-sm" onClick={() => setIsEditing(true)}>
                            Assign
                        </Button>
                    ) : null}
                </Group>
            )}
        </Stack>
    )
}
